import { useSearch } from "@tanstack/react-router";
import { Button, Col, Input, Row, Select, Switch } from "antd";
import Title from "antd/es/typography/Title";
import { useGetConnectionSequenceStepsQuery } from "api/TenantSlug/ConnectionSequenceStep/hooks.ts";
import { OutgoingPostbackTestDrawer } from "components/Drawers/OutgoingPostbackTestDrawer/OutgoingPostbackTestDrawer.tsx";
import { DrawerLink } from "components/UI/DrawerLink";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { AdvertiserSelectSearchable } from "components/UI/Selects/AdvertiserSelectSearchable/AdvertiserSelectSearchable.tsx";
import { CascadeSelectSearchable } from "components/UI/Selects/CascadeSelectSearchable/CascadeSelectSearchable.tsx";
import { ConversionTypeSelectSearchable } from "components/UI/Selects/ConversionTypeSelectSearchable/ConversionTypeSelectSearchable.tsx";
import { CountrySelectSearchable } from "components/UI/Selects/CountrySelectSearchable/CountrySelectSearchable.tsx";
import { FormStructureSelectSearchable } from "components/UI/Selects/FormStructureSelectSearchable/FormStructureSelectSearchable.tsx";
import { IntegrationSelectSearchable } from "components/UI/Selects/IntegrationSelectSearchable/IntegrationSelectSearchable.tsx";
import { OfferSelectSearchable } from "components/UI/Selects/OfferSelectSearchable/OfferSelectSearchable.tsx";
import { PartnerSelectSearchable } from "components/UI/Selects/PartnerSelectSearchable/PartnerSelectSearchable.tsx";
import { PayoutResultSelectSearchable } from "components/UI/Selects/PayoutResultSelectSearchable/PayoutResultSelectSearchable.tsx";
import { PayoutTypeSelectSearchable } from "components/UI/Selects/PayoutTypeSelectSearchable/PayoutTypeSelectSearchable.tsx";
import { SourceSelectSearchable } from "components/UI/Selects/SourceSelectSearchable/SourceSelectSearchable.tsx";
import { StatusSelectSearchable } from "components/UI/Selects/StatusSelectSearchable/StatusSelectSearchable.tsx";
import { TriggerSelectSearchable } from "components/UI/Selects/TriggerSelectSearchable/TriggerSelectSearchable.tsx";
import { UserSelectSearchable } from "components/UI/Selects/UserSelectSearchable/UserSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser.ts";
import { MULTISELECT_ALL_VALUE } from "localConstants";
import { useEffect, useRef } from "react";
import { FormItem } from "react-hook-form-antd";
import { PermissionEnum, RoleEnum } from "types/enums";
import { DrawerId } from "types/models";

const { Option } = Select;

import OutgoingPostbackData = App.Data.OutgoingPostbackData;

interface OutgoingPostbackGeneralFormSectionProps {
    outgoingPostback: OutgoingPostbackData;
}
interface SearchParams {
    [`outgoingPostbackTest-id`]: string;
}

const hasSameValues = <T,>(current: T[], next: T[]) =>
    current.length === next.length && current.every((value, index) => value === next[index]);

export const OutgoingPostbackGeneralFormSection = (props: OutgoingPostbackGeneralFormSectionProps) => {
    const { control, fieldNames, watch, setValue } = useGeneralForm();
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const permissionsUtils = usePermission();
    const userUtils = useUser();
    const currentUser = userUtils.getAuthenticatedUser();
    const isAffiliatePartner =
        userUtils.getActiveTenant()?.userRoles?.some((role) => role.name === RoleEnum.AffiliatePartner) ?? false;
    const canSetOwner = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackSetOwner);
    const canSetAffiliate = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackSetAffiliate);
    const canSetAdvertiser = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackSetAdvertiser);
    const canSetIntegration = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackSetIntegration);
    const canSetChannel = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackSetChannel);

    const includedOffers = watch("offer_ids") ?? [];
    const excludedOffers = watch("excluded_offer_ids") ?? [];
    const includedIsAll = includedOffers.includes(MULTISELECT_ALL_VALUE);
    const excludedIsAll = excludedOffers.includes(MULTISELECT_ALL_VALUE);
    const includedHasSpecific = includedOffers.length > 0 && !includedIsAll;
    const excludedHasSpecific = excludedOffers.length > 0 && !excludedIsAll;
    const previousIncludedIsAll = useRef(includedIsAll);
    const previousExcludedIsAll = useRef(excludedIsAll);

    useEffect(() => {
        const wasIncludedAll = previousIncludedIsAll.current;
        const wasExcludedAll = previousExcludedIsAll.current;

        if (!includedOffers.length && !excludedOffers.length) {
            const nextIncludedOffers =
                wasExcludedAll && !wasIncludedAll ? [MULTISELECT_ALL_VALUE] : ([] as typeof includedOffers);
            const nextExcludedOffers =
                wasIncludedAll && !wasExcludedAll ? [MULTISELECT_ALL_VALUE] : ([] as typeof excludedOffers);

            if (!hasSameValues(includedOffers, nextIncludedOffers)) {
                setValue("offer_ids", nextIncludedOffers);
            }

            if (!hasSameValues(excludedOffers, nextExcludedOffers)) {
                setValue("excluded_offer_ids", nextExcludedOffers);
            }
        } else if (includedIsAll && excludedIsAll) {
            if (!wasIncludedAll && wasExcludedAll && excludedOffers.length > 0) {
                setValue("excluded_offer_ids", []);
            } else if (!wasExcludedAll && wasIncludedAll && includedOffers.length > 0) {
                setValue("offer_ids", []);
            }
        }

        previousIncludedIsAll.current = includedIsAll;
        previousExcludedIsAll.current = excludedIsAll;
    }, [excludedIsAll, excludedOffers, includedIsAll, includedOffers, setValue]);

    const getConnectionSequenceStepsOutgoingQuery = useGetConnectionSequenceStepsQuery({
        filter: {
            filter: { outgoing_postback_id: props.outgoingPostback.id },
        },
    });
    const steps = getConnectionSequenceStepsOutgoingQuery.data?.connectionSequenceSteps ?? [];

    const { t } = usePaldockTranslation("forms.outgoing-postback-general");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tC } = usePaldockTranslation("common");

    return (
        <>
            <div className="flex flex-col gap-y-6">
                <SectionContentWrapper withBackground>
                    <Title level={3} className="!mb-6">
                        {tC("settings")}
                    </Title>
                    <Row gutter={16}>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.name?.name || "name"}
                                label={tFields("name.label")}
                            >
                                <Input placeholder={tFields("name.placeholder")} />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.is_active?.name || "is_active"}
                                label={tFields("status.label")}
                            >
                                <StatusSelectSearchable />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.first_sequence_step_id?.name || "first_sequence_step_id"}
                                label={t("fields.first_step.label")}
                            >
                                <Select placeholder={t("fields.first_step.placeholder")}>
                                    {steps?.map(({ name, id }) => (
                                        <Option value={id} key={id}>
                                            {name}
                                        </Option>
                                    ))}
                                </Select>
                            </FormItem>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.trigger?.name || "trigger"}
                                label={tFields("trigger.label")}
                            >
                                <TriggerSelectSearchable />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.country_id?.name || "country_id"}
                                label={tFields("country.label")}
                            >
                                <CountrySelectSearchable allowClear />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.use_new_connection_creator?.name || "use_new_connection_creator"}
                                label={t("fields.use_new_connection_creator.label")}
                                valuePropName="checked"
                            >
                                <Switch />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.form_structure_ids?.name || "form_structure_ids"}
                                label={tFields("form_structure.label")}
                            >
                                <FormStructureSelectSearchable
                                    mode="multiple"
                                    allowClear
                                    placeholder={tFields("form_structure.placeholder")}
                                />
                            </FormItem>
                        </Col>
                    </Row>
                    {canSetOwner && (
                        <Row gutter={16}>
                            <Col span={8}>
                                <FormItem
                                    control={control}
                                    name={fieldNames.owner_id?.name || "owner_id"}
                                    label={tFields("owner.label")}
                                >
                                    <UserSelectSearchable allowClear={false} />
                                </FormItem>
                            </Col>
                        </Row>
                    )}
                    <Row gutter={16}>
                        <Col span={24}>
                            <FormItem
                                control={control}
                                name={fieldNames.tags?.name || "tags"}
                                label={t("fields.tags.label")}
                            >
                                <Select
                                    mode="multiple"
                                    allowClear
                                    disabled
                                    placeholder={t("fields.tags.placeholder")}
                                />
                            </FormItem>
                        </Col>
                    </Row>

                    {!props.outgoingPostback.use_new_connection_creator && (
                        <Row gutter={8} className="mt-3">
                            <Col span={6}>
                                <div className={"flex items-center gap-x-3"}>
                                    <Button type="primary" size={"middle"}>
                                        {t("buttons.log")}
                                    </Button>
                                    <DrawerLink
                                        drawerId={DrawerId.OutgoingPostbackTest}
                                        search={{
                                            [`${DrawerId.OutgoingPostbackTest}-id`]: props.outgoingPostback.id ?? "",
                                        }}
                                        action="show"
                                    >
                                        <Button type="primary" size={"middle"}>
                                            {t("buttons.run")}
                                        </Button>
                                    </DrawerLink>
                                </div>
                            </Col>
                        </Row>
                    )}
                </SectionContentWrapper>
                <SectionContentWrapper withBackground>
                    <Title level={3} className="!mb-6">
                        {t("sections.conditions")}
                    </Title>
                    <Row gutter={16}>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.sources?.name || "sources"}
                                label={tFields("sources.label")}
                            >
                                <SourceSelectSearchable mode="multiple" allowClear />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.types?.name || "types"}
                                label={tFields("type.label")}
                            >
                                <ConversionTypeSelectSearchable mode="multiple" allowClear />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.affiliate_ids?.name || "affiliate_ids"}
                                label={tFields("affiliates.label")}
                            >
                                {canSetAffiliate ? (
                                    <PartnerSelectSearchable mode="multiple" withAllOption />
                                ) : (
                                    <Select
                                        disabled
                                        mode="multiple"
                                        options={
                                            isAffiliatePartner && currentUser
                                                ? [
                                                      {
                                                          label: currentUser.nickname ?? currentUser.email,
                                                          value: currentUser.id,
                                                      },
                                                  ]
                                                : []
                                        }
                                    />
                                )}
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.advertiser_ids?.name || "advertiser_ids"}
                                label={tFields("advertisers.label")}
                            >
                                {canSetAdvertiser ? (
                                    <AdvertiserSelectSearchable mode="multiple" withAllOption />
                                ) : (
                                    <Select
                                        disabled
                                        mode="multiple"
                                        options={[{ label: tC("all"), value: MULTISELECT_ALL_VALUE }]}
                                    />
                                )}
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.integration_ids?.name || "integration_ids"}
                                label={tFields("integrations.label")}
                            >
                                {canSetIntegration ? (
                                    <IntegrationSelectSearchable mode="multiple" withAllOption />
                                ) : (
                                    <Select
                                        disabled
                                        mode="multiple"
                                        options={[{ label: tC("all"), value: MULTISELECT_ALL_VALUE }]}
                                    />
                                )}
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.cascade_ids?.name || "cascade_ids"}
                                label={t("fields.cascade_ids.label")}
                            >
                                {canSetChannel ? (
                                    <CascadeSelectSearchable mode="multiple" withAllOption />
                                ) : (
                                    <Select
                                        disabled
                                        mode="multiple"
                                        options={[{ label: tC("all"), value: MULTISELECT_ALL_VALUE }]}
                                    />
                                )}
                            </FormItem>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <FormItem
                                control={control}
                                name={fieldNames.offer_ids?.name || "offer_ids"}
                                label={tFields("included_offers.label")}
                            >
                                <OfferSelectSearchable mode="multiple" withAllOption disabled={excludedHasSpecific} />
                            </FormItem>
                        </Col>
                        <Col span={12}>
                            <FormItem
                                control={control}
                                name={fieldNames.excluded_offer_ids?.name || "excluded_offer_ids"}
                                label={tFields("excluded_offers.label")}
                            >
                                <OfferSelectSearchable mode="multiple" withAllOption disabled={includedHasSpecific} />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <FormItem
                                control={control}
                                name={fieldNames.payout_result?.name || "payout_result"}
                                label={t("fields.payout_result.label")}
                            >
                                <PayoutResultSelectSearchable allowClear />
                            </FormItem>
                        </Col>
                        <Col span={12}>
                            <FormItem
                                control={control}
                                name={fieldNames.payout_type?.name || "payout_type"}
                                label={t("fields.payout_type.label")}
                            >
                                <PayoutTypeSelectSearchable allowClear />
                            </FormItem>
                        </Col>
                    </Row>
                </SectionContentWrapper>
            </div>
            {searchParams[`${DrawerId.OutgoingPostbackTest}-id`] && <OutgoingPostbackTestDrawer />}
        </>
    );
};
